import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Chip,
    Divider,
    CircularProgress
} from '@mui/material';

const OrderPreview = ({
    open,
    onClose,
    onConfirm,
    orderData,
    customerDescription,
    translation,
    isLoading = false // הוספת prop למצב טעינה
}) => {
    if (!orderData) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('he-IL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const getDeliveryAddress = () => {
        if (orderData.site) {
            return orderData.site;
        }

        const { address } = orderData;
        if (!address) return 'לא צוינה כתובת';

        const parts = [
            address.street,
            address.hoNumber,
            address.city
        ].filter(Boolean);

        return parts.join(', ') || 'לא צוינה כתובת';
    };

    const getTotalQuantity = () => {
        return orderData.selectedProducts.reduce((total, product) => total + (product.quantity || 0), 0);
    };

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose} // מנע סגירה במצב טעינה
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, maxHeight: '90vh' }
            }}
        >
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    py: 2
                }}
            >
                אישור הזמנה - תצוגה מקדימה
            </DialogTitle>

            <DialogContent sx={{ padding: 2, opacity: isLoading ? 0.7 : 1 }}>
                <Grid container spacing={2}>
                    {/* פרטי לקוח ואספקה */}
                    <Grid item xs={12} md={8}>
                        <Card elevation={2} sx={{ height: '100%' }}>
                            <CardContent sx={{ pb: 2 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#ed6c02' }}>
                                    פרטי ההזמנה
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            לקוח מזמין:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="600">
                                            {customerDescription?.orderingCustomer?.description}
                                        </Typography>
                                    </Box>

                                    {orderData.selectedRecivingCustomer && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                לקוח מקבל:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="600">
                                                {customerDescription?.receivingCustomer?.description}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 1 }} />

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            כתובת אספקה:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="600">
                                            {getDeliveryAddress()}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            תאריך אספקה:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="600" sx={{ color: '#ed6c02' }}>
                                            {formatDate(orderData.deliveryDate)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* סיכום מהיר */}
                    <Grid item xs={12} md={4}>
                        <Card elevation={2} sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent sx={{ textAlign: 'center', pb: 2 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#ed6c02' }}>
                                    סיכום ההזמנה
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography variant="h3" sx={{ color: '#ed6c02', fontWeight: 'bold' }}>
                                            {orderData.selectedProducts.length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            סוגי מוצרים
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="h3" sx={{ color: '#ed6c02', fontWeight: 'bold' }}>
                                            {getTotalQuantity()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            יחידות סה"כ
                                        </Typography>
                                    </Box>

                                    {orderData.selectedFiles && orderData.selectedFiles.length > 0 && (
                                        <Box>
                                            <Typography variant="h4" sx={{ color: '#ed6c02', fontWeight: 'bold' }}>
                                                {orderData.selectedFiles.length}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                קבצים מצורפים
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* רשימת מוצרים */}
                    <Grid item xs={12}>
                        <Card elevation={2}>
                            <CardContent sx={{ pb: 1 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#ed6c02' }}>
                                    מוצרים בהזמנה
                                </Typography>

                                <Box sx={{
                                    maxHeight: orderData.selectedProducts.length > 3 ? 200 : 'auto',
                                    overflow: 'auto',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: '#fafafa'
                                }}>
                                    <List dense sx={{ py: 0 }}>
                                        {orderData.selectedProducts.map((product, index) => (
                                            <ListItem
                                                key={index}
                                                sx={{
                                                    borderBottom: index < orderData.selectedProducts.length - 1 ? '1px solid #e9e9e9' : 'none',
                                                    py: 1
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" fontWeight="500">
                                                            {product.PARTDES}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="text.secondary">
                                                            {product.PARTNAME}
                                                        </Typography>
                                                    }
                                                />
                                                <Chip
                                                    label={product.quantity}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#ed6c02',
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        minWidth: '40px'
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* מצב טעינה - overlay */}
                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 1000,
                            borderRadius: 2
                        }}
                    >
                        <CircularProgress
                            size={60}
                            sx={{ color: '#ed6c02', mb: 2 }}
                        />
                        <Typography
                            variant="h6"
                            sx={{ color: '#ed6c02', fontWeight: 'bold', textAlign: 'center' }}
                        >
                            שולח הזמנה...
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', mt: 1 }}
                        >
                            אנא המתן, הפעולה עלולה להימשך כמה שניות
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ padding: 2, backgroundColor: '#f5f5f5', gap: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    size="large"
                    disabled={isLoading}
                    sx={{
                        color: isLoading ? 'rgba(0, 0, 0, 0.26)' : '#ed6c02',
                        borderColor: isLoading ? 'rgba(0, 0, 0, 0.12)' : '#ed6c02',
                        '&:hover': !isLoading ? {
                            backgroundColor: '#fff5f5',
                            borderColor: '#d85a00'
                        } : {},
                        minWidth: 140,
                        fontWeight: 'bold'
                    }}
                >
                    חזרה לעריכה
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{
                        backgroundColor: isLoading ? 'rgba(0, 0, 0, 0.12)' : '#ed6c02',
                        color: isLoading ? 'rgba(0, 0, 0, 0.26)' : '#fff',
                        fontWeight: 'bold',
                        '&:hover': !isLoading ? {
                            backgroundColor: '#d85a00'
                        } : {},
                        minWidth: 140,
                        position: 'relative'
                    }}
                >
                    {isLoading && (
                        <CircularProgress
                            size={20}
                            sx={{
                                color: '#ed6c02',
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                marginLeft: '-10px',
                                marginTop: '-10px'
                            }}
                        />
                    )}
                    <span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
                        אשר ושלח הזמנה
                    </span>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderPreview;